import { useState, useEffect } from 'react';
import { Heart, Scroll, Feather } from 'lucide-react';
import confetti from 'canvas-confetti';
import { CONTRACT, HER_NAME, MY_NAME } from '@/config/valentineContent';

const clauses = CONTRACT.clauses;
const allItems = clauses.flatMap(c => c.items);
const REQUIRED = CONTRACT.requiredToSign;

const toRoman = (n) => {
  const map = [[10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let r = '';
  for (const [val, sym] of map) { while (n >= val) { r += sym; n -= val; } }
  return r;
};

export default function LoveContract() {
  const [checked, setChecked] = useState({});
  const [signed, setSigned] = useState(false);
  const [sealVisible, setSealVisible] = useState(false);
  const [glowing, setGlowing] = useState(false);
  const [warning, setWarning] = useState(null);

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const progress = Math.min(checkedCount / REQUIRED, 1);

  useEffect(() => {
    if (signed) {
      const t1 = setTimeout(() => setSealVisible(true), 1200);
      const t2 = setTimeout(() => setGlowing(true), 2000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [signed]);

  const handleSign = () => {
    if (checkedCount < REQUIRED) {
      const msg = `Pick at least ${REQUIRED} promises first, ${HER_NAME}`;
      setWarning(msg);
      setTimeout(() => setWarning(null), 3000);
      return;
    }
    setSigned(true);
    setTimeout(() => {
      confetti({ particleCount: 80, spread: 60, origin: { y: 0.8, x: 0.5 }, colors: ['#E91E63', '#F48FB1', '#FF80AB', '#FCE4EC'] });
    }, 400);
    setTimeout(() => {
      confetti({ particleCount: 50, spread: 90, origin: { y: 0.6, x: 0.3 }, colors: ['#E91E63', '#F48FB1', '#FF80AB'] });
      confetti({ particleCount: 50, spread: 90, origin: { y: 0.6, x: 0.7 }, colors: ['#E91E63', '#F48FB1', '#FF80AB'] });
    }, 1400);
  };

  const toggle = (key) => {
    if (signed) return;
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  let globalIdx = 0;

  return (
    <section id="love-contract" data-testid="love-contract-section" className="section-romantic">
      <div className="max-w-2xl mx-auto">
        {/* Outer ornamental frame */}
        <div
          className={`contract-frame relative rounded-3xl p-1 transition-all duration-1000 ${glowing ? 'contract-glow' : ''}`}
          style={{
            background: glowing
              ? 'linear-gradient(135deg, var(--accent-primary), var(--accent-light), var(--accent-primary))'
              : 'var(--accent-glow)'
          }}
        >
          {/* Inner document */}
          <div
            className="contract-paper relative rounded-2xl p-6 sm:p-8 md:p-10"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            {/* Corner ornaments */}
            <div className="contract-corner contract-corner-tl" style={{ color: 'var(--accent-light)' }}>&#10087;</div>
            <div className="contract-corner contract-corner-tr" style={{ color: 'var(--accent-light)' }}>&#10087;</div>
            <div className="contract-corner contract-corner-bl" style={{ color: 'var(--accent-light)' }}>&#10087;</div>
            <div className="contract-corner contract-corner-br" style={{ color: 'var(--accent-light)' }}>&#10087;</div>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-3">
                <Scroll size={28} style={{ color: 'var(--accent-primary)' }} />
              </div>
              <div className="contract-divider mb-4" />
              <h2
                className="font-heading text-xl sm:text-2xl md:text-3xl tracking-wider uppercase mb-1"
                style={{ color: 'var(--text-primary)' }}
              >
                Official Love Contract
              </h2>
              <p
                className="font-body text-[11px] tracking-widest uppercase"
                style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
              >
                Established {new Date().getFullYear()}
              </p>
              <div className="contract-divider mt-4" />
            </div>

            {/* Parties */}
            <div className="text-center mb-8">
              <p className="font-body text-xs" style={{ color: 'var(--text-secondary)' }}>
                {CONTRACT.partyIntro}
              </p>
              <div className="flex items-center justify-center gap-3 mt-2 mb-1">
                <span className="contract-name-fancy font-cursive text-2xl sm:text-3xl" data-testid="contract-name-my">
                  {MY_NAME}
                </span>
                <span className="font-heading text-base" style={{ color: 'var(--text-secondary)' }}>&amp;</span>
                <span className="contract-name-fancy font-cursive text-2xl sm:text-3xl" data-testid="contract-name-her" style={{ animationDelay: '0.4s' }}>
                  {HER_NAME}
                </span>
              </div>
              <p className="font-body text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                referred to as &ldquo;{CONTRACT.partyLabel}&rdquo;
              </p>
            </div>

            {/* Progress ring */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="relative w-12 h-12">
                <svg viewBox="0 0 48 48" className="w-full h-full -rotate-90">
                  <circle cx="24" cy="24" r="20" fill="none" strokeWidth="3"
                    style={{ stroke: 'var(--accent-glow)' }} />
                  <circle cx="24" cy="24" r="20" fill="none" strokeWidth="3"
                    strokeLinecap="round"
                    style={{
                      stroke: progress >= 1 ? 'var(--accent-primary)' : 'var(--accent-light)',
                      strokeDasharray: `${progress * 125.6} 125.6`,
                      transition: 'stroke-dasharray 0.4s ease, stroke 0.3s ease',
                    }} />
                </svg>
                <span
                  className="absolute inset-0 flex items-center justify-center font-body text-xs font-bold"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  {checkedCount}
                </span>
              </div>
              <p className="font-body text-xs" style={{ color: 'var(--text-secondary)' }}>
                {checkedCount < REQUIRED
                  ? `${REQUIRED - checkedCount} more to sign`
                  : 'Ready to sign!'}
              </p>
            </div>

            {/* Clauses by section */}
            {clauses.map((section, si) => {
              const sectionItems = section.items.map((item, ii) => {
                const key = globalIdx;
                globalIdx++;
                return { item, key, articleNum: key + 1 };
              });
              return (
                <div key={si} className="mb-6">
                  <p
                    className="font-heading text-xs tracking-widest uppercase mb-3 flex items-center gap-2"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    <span className="contract-section-line" />
                    {section.section}
                    <span className="contract-section-line" />
                  </p>
                  <div className="space-y-3">
                    {sectionItems.map(({ item, key, articleNum }) => (
                      <button
                        key={key}
                        data-testid={`contract-clause-${key}`}
                        onClick={() => toggle(key)}
                        disabled={signed}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-300 group ${signed ? 'cursor-default' : 'cursor-pointer hover:scale-[1.01]'
                          }`}
                        style={{
                          backgroundColor: checked[key] ? 'var(--accent-glow)' : 'transparent',
                          border: checked[key] ? '1px solid var(--accent-light)' : '1px solid transparent',
                        }}
                      >
                        {/* Custom heart checkbox */}
                        <div
                          className={`contract-heart-check flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${checked[key] ? 'contract-heart-checked' : ''
                            }`}
                          style={{
                            backgroundColor: checked[key] ? 'var(--accent-primary)' : 'transparent',
                            border: checked[key] ? 'none' : '2px solid var(--accent-light)',
                          }}
                        >
                          <Heart
                            size={14}
                            fill={checked[key] ? '#fff' : 'none'}
                            strokeWidth={checked[key] ? 0 : 1.5}
                            style={{ color: checked[key] ? '#fff' : 'var(--accent-light)' }}
                            className={`transition-all duration-300 ${checked[key] ? 'scale-100' : 'scale-75 group-hover:scale-100'}`}
                          />
                        </div>
                        {/* Article number + text */}
                        <div className="flex-1 min-w-0">
                          <span className="font-body text-[10px] tracking-wider" style={{ color: 'var(--accent-light)' }}>
                            ARTICLE {toRoman(articleNum)}
                          </span>
                          <p
                            className={`font-body text-sm leading-snug transition-all duration-300 ${checked[key] ? 'translate-x-0' : ''}`}
                            style={{ color: checked[key] ? 'var(--text-primary)' : 'var(--text-secondary)' }}
                          >
                            {item}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="contract-divider my-6" />

            {/* Signature area */}
            {!signed ? (
              <div className="text-center">
                {warning && (
                  <div
                    className="mb-4 mx-auto max-w-sm rounded-xl px-4 py-3 font-body text-sm"
                    style={{
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--accent-primary)',
                    }}
                  >
                    {warning}
                  </div>
                )}
                <button
                  data-testid="sign-contract-btn"
                  onClick={handleSign}
                  className="contract-sign-btn inline-flex items-center gap-2 px-8 py-3 rounded-xl font-body text-sm font-medium text-white transition-all duration-300 hover:scale-105 active:scale-95"
                  style={{
                    background: checkedCount >= REQUIRED
                      ? 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-light) 100%)'
                      : 'var(--accent-light)',
                    boxShadow: checkedCount >= REQUIRED
                      ? '0 8px 30px var(--accent-glow)'
                      : 'none',
                    opacity: checkedCount >= REQUIRED ? 1 : 0.6,
                  }}
                >
                  <Feather size={16} />
                  Sign with Love
                </button>
              </div>
            ) : (
              <div className="relative">
                {/* Signatures */}
                <div className="flex flex-col sm:flex-row items-center justify-around gap-8 mb-6">
                  {/* Royal's signature */}
                  <div className="text-center" data-testid="signature-my">
                    <div className="signature-reveal contract-signed-name">
                      <p className="font-cursive text-3xl md:text-4xl">
                        {MY_NAME}
                      </p>
                    </div>
                    <div className="contract-sig-line mt-2" style={{ borderColor: 'var(--accent-light)' }} />
                    <p className="font-body text-[10px] mt-2" style={{ color: 'var(--text-secondary)' }}>
                      {CONTRACT.signer1Label}
                    </p>
                  </div>
                  {/* Her signature */}
                  <div className="text-center" data-testid="signature-her">
                    <div className="signature-reveal contract-signed-name" style={{ animationDelay: '0.4s' }}>
                      <p className="font-cursive text-3xl md:text-4xl">
                        {HER_NAME}
                      </p>
                    </div>
                    <div className="contract-sig-line mt-2" style={{ borderColor: 'var(--accent-light)' }} />
                    <p className="font-body text-[10px] mt-2" style={{ color: 'var(--text-secondary)' }}>
                      {CONTRACT.signer2Label}
                    </p>
                  </div>
                </div>

                {/* Date */}
                <p className="text-center font-body text-[10px] mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Signed on this {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>

                {/* Wax seal */}
                {sealVisible && (
                  <div className="flex justify-center">
                    <div className="contract-wax-seal">
                      <div className="contract-wax-inner">
                        <Heart size={22} fill="#fff" strokeWidth={0} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
